// YouTube Search Module
// Searches videos from channel playlists.
//
// API calls go through the Cloudflare Worker proxy at /api/youtube/*
// (worker source: terraform/cloudflare/gtowizard/workers/scripts/youtube_api_proxy.js,
// route: blog.gtowizard.com/api/youtube/*).
// The proxy holds the API key as a Worker secret and edge-caches responses
// (24h playlists, 2h playlistItems) so we never put the key in client JS
// and the daily quota is shared across all visitors via the edge cache.
// SearchCache below is the second layer (per-browser localStorage), which
// stacks on top of the edge cache for warm sessions.

const SearchCache = {
  TTL_PLAYLISTS: 24 * 60 * 60 * 1000,
  TTL_VIDEOS: 2 * 60 * 60 * 1000,

  set(key, value, ttl) {
    try {
      localStorage.setItem(key, JSON.stringify({ v: value, exp: Date.now() + ttl }));
    } catch (e) { /* localStorage full or unavailable */ }
  },

  get(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const { v, exp } = JSON.parse(raw);
      if (Date.now() > exp) { localStorage.removeItem(key); return null; }
      return v;
    } catch (e) { return null; }
  },
};

class YouTubeSearch {
  constructor() {
    this.channelId = null;
    this.isInitialized = false;
    this.allPlaylists = [];
    this.allVideos = [];
    this.videosLoaded = false;
    this.loadingPromise = null;
  }

  async init(channelId) {
    if (!channelId) {
      console.warn('YouTube channel ID not provided');
      return;
    }

    if (channelId.includes('{{') || channelId === 'undefined') {
      console.warn('YouTube channel ID not configured');
      return;
    }

    this.channelId = channelId;
    this.isInitialized = true;
    // Videos are loaded lazily on first search, not on init
  }

  async loadAllVideos() {
    if (!this.isInitialized) return;
    if (this.videosLoaded) return;
    // Prevent concurrent loads
    if (this.loadingPromise) return this.loadingPromise;

    this.loadingPromise = this._doLoadAllVideos().finally(() => {
      this.loadingPromise = null;
    });
    return this.loadingPromise;
  }

  async _doLoadAllVideos() {
    // Check full search cache first
    const searchCacheKey = `yt_search_${this.channelId}`;
    const cachedVideos = SearchCache.get(searchCacheKey);
    if (cachedVideos) {
      this.allVideos = cachedVideos;
      this.videosLoaded = true;
      return;
    }

    try {
      // Reuse same playlists cache key as cards.js
      const playlistsCacheKey = `yt_pl_${this.channelId}`;
      let playlists = SearchCache.get(playlistsCacheKey);

      if (!playlists) {
        const playlistsUrl = `/api/youtube/playlists?part=snippet,contentDetails&channelId=${this.channelId}&maxResults=50`;
        const playlistsResponse = await fetch(playlistsUrl);
        const playlistsData = await playlistsResponse.json();

        if (playlistsData.error || !playlistsData.items) {
          console.error('Failed to load playlists:', playlistsData.error);
          return;
        }

        playlists = playlistsData.items
          .map(pl => ({ id: pl.id, title: pl.snippet.title, count: pl.contentDetails?.itemCount || 0 }))
          .filter(pl => pl.title.toLowerCase() !== 'shorts');
        SearchCache.set(playlistsCacheKey, playlists, SearchCache.TTL_PLAYLISTS);
      }

      this.allPlaylists = playlists.filter(pl => pl.title.toLowerCase() !== 'shorts');

      const videoPromises = this.allPlaylists.map(playlist =>
        this.loadPlaylistVideos(playlist.id, playlist.title)
      );

      const videosArrays = await Promise.all(videoPromises);
      this.allVideos = videosArrays.flat();
      this.videosLoaded = true;
      SearchCache.set(searchCacheKey, this.allVideos, SearchCache.TTL_VIDEOS);
    } catch (error) {
      console.error('Failed to load YouTube videos:', error);
    }
  }

  async loadPlaylistVideos(playlistId, playlistTitle) {
    // Reuse per-playlist cache from cards.js if available
    const playlistCacheKey = `yt_pv_${playlistId}`;
    const cached = SearchCache.get(playlistCacheKey);
    if (cached && cached.videos) {
      return cached.videos.map(v => ({
        ...v,
        // Normalize snake_case fields written by cards.js to camelCase used here
        thumbnail: v.thumbnail || v.feature_image || null,
        publishedAt: v.publishedAt || v.published_at || null,
        playlistTitle: playlistTitle || v.playlistTitle || v.playlist_title || null,
        description: v.description || '',
      }));
    }

    try {
      let allVideos = [];
      let pageToken = null;

      do {
        const url = `/api/youtube/playlistItems?part=snippet,contentDetails,status&maxResults=50&playlistId=${playlistId}${pageToken ? `&pageToken=${pageToken}` : ''}`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.error || !data.items) break;

        const videos = data.items
          .filter(item => {
            const title = item.snippet?.title || '';
            if (!title || title === 'Deleted video' || title === 'Private video' || title === 'This video is unavailable') return false;
            if (item.status?.privacyStatus) return item.status.privacyStatus === 'public';
            return true;
          })
          .map(item => ({
            id: item.contentDetails?.videoId || item.snippet?.resourceId?.videoId,
            title: item.snippet.title,
            description: item.snippet.description || '',
            publishedAt: item.snippet.publishedAt,
            playlistTitle: playlistTitle,
            thumbnail: item.snippet.thumbnails?.maxres?.url ||
                       item.snippet.thumbnails?.standard?.url ||
                       item.snippet.thumbnails?.high?.url ||
                       item.snippet.thumbnails?.medium?.url,
            url: `https://www.youtube.com/watch?v=${item.contentDetails?.videoId || item.snippet?.resourceId?.videoId}`
          }));

        allVideos = allVideos.concat(videos);
        pageToken = data.nextPageToken || null;
      } while (pageToken);

      return allVideos;
    } catch (error) {
      console.error(`Failed to load videos from playlist ${playlistId}:`, error);
      return [];
    }
  }

  async search(query, options = {}) {
    if (!this.isInitialized) {
      console.warn('YouTube search not initialized');
      return [];
    }

    const { maxResults = 10 } = options;

    try {
      if (!this.videosLoaded) {
        await this.loadAllVideos();
      }

      if (this.allVideos.length === 0) return [];

      const queryLower = query.toLowerCase();
      const matchingVideos = this.allVideos.filter(video => {
        const titleMatch = video.title.toLowerCase().includes(queryLower);
        const descMatch = video.description.toLowerCase().includes(queryLower);
        return titleMatch || descMatch;
      });

      matchingVideos.sort((a, b) => {
        const aTitleMatch = a.title.toLowerCase().includes(queryLower);
        const bTitleMatch = b.title.toLowerCase().includes(queryLower);
        if (aTitleMatch && !bTitleMatch) return -1;
        if (!aTitleMatch && bTitleMatch) return 1;
        return new Date(b.publishedAt) - new Date(a.publishedAt);
      });

      return matchingVideos.slice(0, maxResults).map(video => ({
        id: video.id,
        title: video.title,
        description: video.description,
        thumbnail: video.thumbnail,
        publishedAt: video.publishedAt,
        playlistTitle: video.playlistTitle,
        url: video.url,
        type: 'youtube'
      }));
    } catch (error) {
      console.error('YouTube search failed:', error);
      return [];
    }
  }

  renderSearchResult(video) {
    const thumbnail = video.thumbnail ?
      `<div class="search-results-item__image">
        <img src="${video.thumbnail}" alt="${video.title}">
      </div>` : '';

    const playlistTag = video.playlistTitle ?
      `<span class="search-results-item__tag">${video.playlistTitle}</span>` : '';

    const date = video.publishedAt ? new Date(video.publishedAt) : null;
    const isValidDate = date && !isNaN(date.getTime());
    const dateStr = isValidDate ? date.toLocaleDateString(undefined, {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    }) : '';

    const playlistTagDesktop = video.playlistTitle ?
      `<span class="search-results-item__tag search-results-item__tag--desktop">${video.playlistTitle}</span>` : '';

    return `
      <a href="${video.url}" class="search-results-item flex items-center" target="_blank" rel="noopener noreferrer">
        <div class="search-results-item__main flex direction-column">
          <div class="search-results-item__tags search-results-item__tags--mobile flex items-center">
            <span class="search-results-item__tag">Video</span>
            ${playlistTag}
          </div>
          <h3 class="search-results-item__title">${video.title}</h3>
          <div class="search-results-item__meta flex items-center">
            <span class="search-results-item__tag search-results-item__tag--desktop">Video</span>
            ${playlistTagDesktop}
            ${isValidDate ? `<span class="search-results-item__date-group"><time datetime="${date.toISOString().split('T')[0]}" class="search-results-item__date">${dateStr}</time></span>` : ''}
          </div>
        </div>
        ${thumbnail}
      </a>
    `;
  }
}

// Export for use in main search module
export default YouTubeSearch;
